import React from 'react';
import '../pages/Interview.css';
import InterviewStepper from './InterviewStepper';

export const INTERVIEW_RESUME_LABELS = Object.freeze({
    eyebrow: 'Interview interrupted',
    heading: 'Your interview was interrupted',
    lede: 'This page was left, reloaded, or closed while your interview was in progress.',
    ledeStarted: (position) => ` Your submitted answers are saved, but the timer for question ${position} kept running while you were away. If its time limit has passed, that question is forfeited.`,
    ledeIdle: ' Your submitted answers are saved and no answer was in progress — your remaining time is unaffected.',
    progressTerm: 'Progress',
    progressValue: (answered, total) => `${answered} of ${total} answered`,
    questionTerm: (position) => `Question ${position}`,
    elapsed: (elapsedLabel) => `${elapsedLabel} elapsed — the clock did not pause`,
    notStarted: 'Not started — full time remaining',
    deviceHeading: 'Check your camera and microphone',
    deviceBody: 'Reloading the page resets device permissions. Re-check your devices so your next answer records correctly.',
    deviceHint: 'Test your camera and microphone above to continue.',
    urgencyHint: 'The timer is still running — resume as soon as your devices are ready.',
    resuming: 'Resuming...',
    resume: (position) => `Resume Question ${position}`,
    continueTo: (position) => `Continue to Question ${position}`,
});

/**
 * Post-interruption resume screen. Shown before the waiting screen when the
 * interview was already started and the page was left/reloaded/closed without
 * the post-submit transition flag. `children` is the shared DeviceCheckPanel.
 * Wizard-card footprint: page-level stepper above the card (Interview active),
 * footer bar with the contextual hint bottom-left and the resume action
 * bottom-right.
 * This screen's copy comes from `labels` shallow-merged over
 * INTERVIEW_RESUME_LABELS; the stepper's group arrives separately as
 * `stepperLabels` and is handed straight to InterviewStepper.
 */
const InterviewResume = ({
    questionPosition,
    totalQuestions,
    answeredCount,
    questionStarted,
    elapsedLabel,
    deviceReady,
    resuming,
    onResume,
    labels = null,
    stepperLabels = null,
    children
}) => {
    const L = { ...INTERVIEW_RESUME_LABELS, ...(labels || {}) };

    return (
        <div className="interview-resume">
            <InterviewStepper activeIndex={3} labels={stepperLabels} />
            <div className="resume-card">
                <p className="resume-eyebrow">{L.eyebrow}</p>
                <h2>{L.heading}</h2>
                <p className="resume-lede">
                    {L.lede}
                    {questionStarted ? L.ledeStarted(questionPosition) : L.ledeIdle}
                </p>

                <dl className="resume-facts">
                    <div className="resume-fact">
                        <dt>{L.progressTerm}</dt>
                        <dd>{L.progressValue(answeredCount, totalQuestions)}</dd>
                    </div>
                    <div className="resume-fact">
                        <dt>{L.questionTerm(questionPosition)}</dt>
                        {questionStarted ? (
                            <dd className="fact-warn">{L.elapsed(elapsedLabel)}</dd>
                        ) : (
                            <dd>{L.notStarted}</dd>
                        )}
                    </div>
                </dl>

                <div className="resume-device-check">
                    <h3>{L.deviceHeading}</h3>
                    <p>{L.deviceBody}</p>
                    {children}
                </div>

                <div className="wizard-footer">
                    <div className="wizard-footer-left">
                        {!deviceReady ? (
                            <p className="wizard-hint">{L.deviceHint}</p>
                        ) : questionStarted ? (
                            <p className="wizard-hint resume-urgency">{L.urgencyHint}</p>
                        ) : null}
                    </div>
                    <button
                        className="btn btn-large"
                        onClick={onResume}
                        disabled={!deviceReady || resuming}
                    >
                        {resuming ? (
                            <>
                                <div className="spinner-small"></div>
                                {L.resuming}
                            </>
                        ) : questionStarted ? (
                            L.resume(questionPosition)
                        ) : (
                            L.continueTo(questionPosition)
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default InterviewResume;
